/* eslint-disable react-refresh/only-export-components -- DimeSidebar* re-exports hooks/helpers alongside components */
import * as React from 'react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  SIDEBAR_COOKIE_NAME,
  useSidebar,
} from './components/ui/sidebar.js';
import { TooltipProvider } from './components/ui/tooltip.js';
import { cn } from './lib/utils.js';

/** Cookie name written by shadcn `SidebarProvider` — use when clearing in tests. */
export const DIME_SIDEBAR_COOKIE_NAME = SIDEBAR_COOKIE_NAME;

/** Seed `DimeSidebarProvider` `defaultOpen` from the persisted desktop cookie. */
export function readDimeSidebarDefaultOpen(): boolean {
  if (typeof document === 'undefined') return true;
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${DIME_SIDEBAR_COOKIE_NAME}=`));
  return cookie?.slice(DIME_SIDEBAR_COOKIE_NAME.length + 1) !== 'false';
}

export function DimeSidebarProvider({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarProvider>>) {
  return (
    <TooltipProvider>
      <SidebarProvider {...props} />
    </TooltipProvider>
  );
}

export { useSidebar as useDimeSidebar };

export function DimeSidebar({ ...props }: Readonly<React.ComponentProps<typeof Sidebar>>) {
  return <Sidebar {...props} />;
}

export function DimeSidebarHeader({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarHeader>>) {
  return <SidebarHeader {...props} />;
}

export function DimeSidebarFooter({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarFooter>>) {
  return <SidebarFooter {...props} />;
}

export function DimeSidebarContent({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarContent>>) {
  return <SidebarContent {...props} />;
}

export function DimeSidebarInset({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarInset>>) {
  return <SidebarInset {...props} />;
}

export function DimeSidebarTrigger({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarTrigger>>) {
  const { open, openMobile, isMobile } = useSidebar();
  const expanded = isMobile ? openMobile : open;
  const defaultLabel = isMobile
    ? expanded
      ? 'Close navigation'
      : 'Open navigation'
    : expanded
      ? 'Collapse sidebar'
      : 'Expand sidebar';

  return (
    <SidebarTrigger
      aria-label={defaultLabel}
      aria-expanded={expanded}
      {...props}
      className={cn('[&_span.sr-only]:hidden', props.className)}
    />
  );
}

export function DimeSidebarRail({ ...props }: Readonly<React.ComponentProps<typeof SidebarRail>>) {
  return <SidebarRail {...props} />;
}

export function DimeSidebarSeparator({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarSeparator>>) {
  return <SidebarSeparator {...props} />;
}

export function DimeSidebarInput({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarInput>>) {
  return <SidebarInput {...props} />;
}

export function DimeSidebarGroup({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarGroup>>) {
  return <SidebarGroup {...props} />;
}

export function DimeSidebarGroupLabel({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarGroupLabel>>) {
  return <SidebarGroupLabel {...props} />;
}

export function DimeSidebarGroupAction({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarGroupAction>>) {
  return <SidebarGroupAction {...props} />;
}

export function DimeSidebarGroupContent({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarGroupContent>>) {
  return <SidebarGroupContent {...props} />;
}

export function DimeSidebarMenu({ ...props }: Readonly<React.ComponentProps<typeof SidebarMenu>>) {
  return <SidebarMenu {...props} />;
}

export function DimeSidebarMenuItem({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuItem>>) {
  return <SidebarMenuItem {...props} />;
}

export function DimeSidebarMenuButton({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuButton>>) {
  return <SidebarMenuButton {...props} />;
}

export function DimeSidebarMenuAction({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuAction>>) {
  return <SidebarMenuAction {...props} />;
}

export function DimeSidebarMenuBadge({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuBadge>>) {
  return <SidebarMenuBadge {...props} />;
}

export function DimeSidebarMenuSkeleton({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuSkeleton>>) {
  return <SidebarMenuSkeleton {...props} />;
}

export function DimeSidebarMenuSub({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuSub>>) {
  return <SidebarMenuSub {...props} />;
}

export function DimeSidebarMenuSubItem({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuSubItem>>) {
  return <SidebarMenuSubItem {...props} />;
}

export function DimeSidebarMenuSubButton({
  ...props
}: Readonly<React.ComponentProps<typeof SidebarMenuSubButton>>) {
  return <SidebarMenuSubButton {...props} />;
}
